import {currentAppId} from '@github-ui/app-uuid'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {ContextRegionPayload} from '@github-ui/global-navigation-types'
import {isReactAppAtLocation, waitForReactAppRender} from '@github-ui/react-app-navigation'
import {type QueryKey, useQueryClient} from '@github-ui/react-query'
import {softNavigate} from '@github-ui/soft-navigate'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useLayoutEffect, useRef, useState} from 'react'

import {
  getCurrentLocationPath,
  getGlobalNavContextRefreshDescriptor,
  getNavigationError,
  type GlobalNavContextRefreshDescriptor,
  isLocationInScope,
  reloadIfAtLocation,
} from './context-region-navigation-state'
import {
  type GlobalNavigationRefreshOptions,
  type GlobalNavigationRefreshResult,
  registerGlobalNavigationRefreshHandler,
} from './refresh-global-navigation'
import {applyRepositoryMetaTags, readRepositoryMetaTags} from './repository-meta-tags'

// Long enough to cover moving back and forth between pages, short enough to keep tab counts current.
export const CONTEXT_REGION_STALE_TIME_MS = 60_000

function contextRegionQueryKey(requestUrl: string): QueryKey {
  return ['global-nav-context-region', requestUrl]
}

interface UseContextRegionRefreshOptions {
  contextRegion?: ContextRegionPayload
  contextRegionUrl?: string | null
}

export function useContextRegionRefresh({contextRegion, contextRegionUrl}: UseContextRegionRefreshOptions) {
  const [currentContextRegion, setCurrentContextRegion] = useState(contextRegion)
  const [syncedContextRegion, setSyncedContextRegion] = useState(contextRegion)
  const queryClient = useQueryClient()
  const requestGenerationRef = useRef(0)
  const inFlightRequestUrlRef = useRef<string | null>(null)
  // Scope of the context applied client-side since the last server-rendered context region.
  const appliedScopeRef = useRef<string | undefined>(undefined)
  // Where and when the displayed context region was server-rendered, until a refresh replaces it.
  const serverRenderedContextRef = useRef<{path: string; renderedAt: number} | undefined>(undefined)
  const cancelInFlightRequest = useCallback(() => {
    const inFlightRequestUrl = inFlightRequestUrlRef.current
    if (inFlightRequestUrl) void queryClient.cancelQueries({queryKey: contextRegionQueryKey(inFlightRequestUrl)})
    inFlightRequestUrlRef.current = null
  }, [queryClient])
  const invalidateRefresh = useCallback(() => {
    requestGenerationRef.current++
    cancelInFlightRequest()
  }, [cancelInFlightRequest])

  // A new `contextRegion` prop comes from the parent route, so it is authoritative: it supersedes
  // any refresh that is still in flight, and it must be shown even if an earlier refresh failed.
  const hasNewContextRegionProp = !Object.is(syncedContextRegion, contextRegion)
  if (hasNewContextRegionProp) {
    setSyncedContextRegion(contextRegion)
    setCurrentContextRegion(contextRegion)
  }

  const activeContextRegion = hasNewContextRegionProp ? contextRegion : currentContextRegion
  const activeContextRegionRef = useRef(activeContextRegion)
  useLayoutEffect(() => {
    activeContextRegionRef.current = activeContextRegion
  })

  const refreshContextRegion = useCallback(
    async ({
      path,
      crumbType,
      crumbObjectIdentifier,
      selectedLink,
      scope,
      navigationCompletion,
      currentContext,
    }: GlobalNavigationRefreshOptions): Promise<GlobalNavigationRefreshResult> => {
      const generation = ++requestGenerationRef.current
      const navigationError = getNavigationError(navigationCompletion)
      const request =
        contextRegionUrl &&
        getContextRegionRequest(contextRegionUrl, {path, crumbType, crumbObjectIdentifier, selectedLink})

      // A refresh for the same destination reuses the in-flight request instead of cancelling it.
      if (!request || request.url !== inFlightRequestUrlRef.current) cancelInFlightRequest()
      if (!contextRegionUrl || !request) return 'failed'

      // Cache the server-rendered context so returning to it doesn't need a request. Refreshed contexts are already
      // cached. Skip a displayed context that `currentContext` doesn't describe, such as while a Back/Forward
      // restore is still pending, so another page's context is never cached under this key.
      const displayedContextRegion = activeContextRegionRef.current
      const serverRenderedContext = serverRenderedContextRef.current
      const currentRequest = currentContext && getContextRegionRequest(contextRegionUrl, currentContext)
      if (
        currentRequest &&
        displayedContextRegion &&
        serverRenderedContext &&
        (displayedContextRegion.currentPath ?? serverRenderedContext.path) === currentRequest.destinationPath
      ) {
        const currentQueryKey = contextRegionQueryKey(currentRequest.url)
        const cachedAt = queryClient.getQueryState(currentQueryKey)?.dataUpdatedAt ?? 0
        if (cachedAt < serverRenderedContext.renderedAt) {
          // Keep the render time, so the server-rendered context expires as if it had been fetched then.
          queryClient.setQueryData<ContextRegionPayload>(
            currentQueryKey,
            {
              ...displayedContextRegion,
              currentPath: currentRequest.destinationPath,
              // Like a refresh response, include the page's repository meta tags so returning restores them.
              ...(currentContext.crumbType === 'repository' ? {meta: readRepositoryMetaTags()} : {}),
            },
            {updatedAt: serverRenderedContext.renderedAt},
          )
        }
      }

      const applyAfterNavigation = async (payload: ContextRegionPayload): Promise<GlobalNavigationRefreshResult> => {
        if ((await navigationError) !== undefined) return 'failed'
        if (generation !== requestGenerationRef.current) return 'stale'
        appliedScopeRef.current = scope
        serverRenderedContextRef.current = undefined
        setCurrentContextRegion(payload)
        if (payload.meta) applyRepositoryMetaTags(payload.meta)
        return 'applied'
      }

      inFlightRequestUrlRef.current = request.url
      let payload: ContextRegionPayload
      try {
        // Fresh cached responses resolve without a request. Failed requests throw and are not cached.
        payload = await queryClient.fetchQuery({
          queryKey: contextRegionQueryKey(request.url),
          queryFn: async ({signal}) => {
            const response = await reactFetchJSON(request.url, {signal})
            if (!response.ok) throw new Error(`Context region request failed with ${response.status}`)
            const result = (await response.json()) as ContextRegionPayload
            if (result.currentPath !== request.destinationPath) throw new Error('Context region path mismatch')
            return result
          },
          staleTime: CONTEXT_REGION_STALE_TIME_MS,
          retry: false,
        })
      } catch {
        if (generation === requestGenerationRef.current) inFlightRequestUrlRef.current = null
        return generation === requestGenerationRef.current ? 'failed' : 'stale'
      }
      if (generation === requestGenerationRef.current) inFlightRequestUrlRef.current = null
      if (generation !== requestGenerationRef.current) return 'stale'
      return applyAfterNavigation(payload)
    },
    [cancelInFlightRequest, contextRegionUrl, queryClient],
  )

  useLayoutEffect(() => {
    const unregister = registerGlobalNavigationRefreshHandler(refreshContextRegion)
    const refreshFromDescriptor = async (descriptor: GlobalNavContextRefreshDescriptor) => {
      const result = await refreshContextRegion({
        ...descriptor,
        // The React app renders after history moves, and this navigation bar is a separate React root.
        navigationCompletion: waitForReactAppRender(() => isReactAppAtLocation(descriptor.path)),
      })
      if (result === 'failed') reloadIfAtLocation(descriptor.path)
    }
    const refreshAfterHistoryNavigation = (event: PopStateEvent) => {
      if (!isFeatureEnabled('global_nav_context_refresh')) return

      const descriptor = getGlobalNavContextRefreshDescriptor(event.state)
      if (descriptor) {
        void refreshFromDescriptor(descriptor)
        return
      }

      invalidateRefresh()
      // Entries without a descriptor keep the displayed context, which is only correct within its scope.
      // Turbo restores entries owned by another app, so only reload entries that the mounted React app handles.
      const appliedScope = appliedScopeRef.current
      const appId = (event.state as {appId?: unknown} | null)?.appId
      const isReactAppEntry = appId !== 'rails' && appId === currentAppId()
      if (appliedScope && isReactAppEntry && !isLocationInScope(appliedScope)) {
        softNavigate(getCurrentLocationPath())
      }
    }
    window.addEventListener('popstate', refreshAfterHistoryNavigation, true)
    return () => {
      window.removeEventListener('popstate', refreshAfterHistoryNavigation, true)
      unregister()
      invalidateRefresh()
    }
  }, [invalidateRefresh, refreshContextRegion])

  useLayoutEffect(() => {
    // Invalidate and release any refresh that is still in flight, so a response for an older
    // destination or endpoint can't overwrite the current context.
    invalidateRefresh()
    appliedScopeRef.current = undefined
    serverRenderedContextRef.current = {
      path: `${window.location.pathname}${window.location.search}`,
      renderedAt: Date.now(),
    }
  }, [contextRegion, contextRegionUrl, invalidateRefresh])

  return {activeContextRegion}
}

interface ContextRegionRequestOptions {
  path: string
  crumbType?: string
  crumbObjectIdentifier?: string
  selectedLink?: string
}

/** Builds the context region request URL, which also identifies the response in the cache. */
function getContextRegionRequest(
  contextRegionUrl: string,
  {path, crumbType, crumbObjectIdentifier, selectedLink}: ContextRegionRequestOptions,
): {url: string; destinationPath: string} | undefined {
  let destination: URL
  let url: URL
  try {
    destination = new URL(path, window.location.origin)
    url = new URL(contextRegionUrl, window.location.origin)
  } catch {
    return undefined
  }

  if (destination.origin !== window.location.origin) return undefined
  const destinationPath = `${destination.pathname}${destination.search}`

  url.searchParams.set('path', destinationPath)
  if (crumbType) url.searchParams.set('crumb_type', crumbType)
  if (crumbObjectIdentifier) url.searchParams.set('crumb_object_identifier', crumbObjectIdentifier)
  if (selectedLink) url.searchParams.set('selected_link', selectedLink)
  return {url: url.toString(), destinationPath}
}
